import { useState, useEffect, useCallback } from 'react';
import { useRoute, useLocation } from 'wouter';
import { ArrowLeft, Check, Eye, EyeOff, Loader2, PanelLeft, Pencil, Save, SlidersHorizontal, Undo2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TemplateFieldEditor, type TemplateFieldEditorToolbarState } from '@/components/templates/TemplateFieldEditor';
import { apiClient } from '@/lib/api-client';
import { fetchPdfBlob } from '@/lib/pdf-blob-fetcher';
import { env } from '@/config/env';
import { auth } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';
import type { FieldPlacement } from '@/types/template-fields';
import type { FormTemplate } from '@/api/generated/client';

export default function TemplateEditor() {
  const [, params] = useRoute('/templates/:id/editor');
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [initialPlacements, setInitialPlacements] = useState<FieldPlacement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [editorToolbar, setEditorToolbar] = useState<TemplateFieldEditorToolbarState | null>(null);

  const templateId = params?.id ? parseInt(params.id) : null;

  // Load template, PDF, and field placements
  useEffect(() => {
    if (!templateId) {
      setError('Invalid template ID');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadData() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch template info
        const tmpl = await apiClient.formTemplates.getFormTemplateById({ id: templateId! });
        if (cancelled) return;
        setTemplate(tmpl);

        // Fetch PDF as blob using custom utility with proper cache control
        // This avoids 304 responses with empty bodies that cause "Failed to load PDF"
        const getToken = async () => {
          if (auth.currentUser) {
            return await auth.currentUser.getIdToken();
          }
          throw new Error('Not authenticated');
        };
        const pdfBlob = await fetchPdfBlob(`/api/form-templates/${templateId}/pdf`, env.apiBaseUrl, getToken);
        if (cancelled) return;
        const url = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(url);

        // Fetch field placements using the API client
        const placementsResponse = await apiClient.formTemplates.getFieldPlacements({ id: templateId! });
        if (!cancelled) {
          setInitialPlacements(placementsResponse.fieldPlacements ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load template data:', err);
          setError('Failed to load template. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const handleSave = useCallback(
    async (placements: FieldPlacement[]) => {
      if (!templateId) return;

      try {
        const request = {
          id: templateId,
          updateFieldPlacementsRequest: {
            fieldPlacements: placements,
          },
        };
        await apiClient.formTemplates.updateFieldPlacements(request);

        toast({
          title: 'Saved',
          description: 'Field placements saved successfully.',
        });
      } catch (error) {
        const errMessage = error instanceof Error ? error.message : 'Failed to save field placements';
        toast({
          title: 'Error',
          description: errMessage,
          variant: 'destructive',
        });
        throw error;
      }
    },
    [templateId, toast],
  );

  const startRenaming = () => {
    if (!template) return;
    setTemplateName(template.name);
    setIsEditingName(true);
  };

  const cancelRenaming = () => {
    setIsEditingName(false);
    setTemplateName(template?.name ?? '');
  };

  const saveTemplateName = async () => {
    if (!template || !templateId) {
      cancelRenaming();
      return;
    }

    const name = templateName.trim();
    if (!name || name === template.name) {
      cancelRenaming();
      return;
    }

    setIsSavingName(true);
    try {
      const updatedTemplate = await apiClient.formTemplates.updateFormTemplate({
        id: templateId,
        updateFormTemplateRequest: { name },
      });
      setTemplate(updatedTemplate);
      setIsEditingName(false);
      toast({ title: 'Template renamed' });
    } catch (saveError) {
      const description = saveError instanceof Error ? saveError.message : 'Failed to rename template.';
      toast({
        title: 'Rename failed',
        description,
        variant: 'destructive',
      });
    } finally {
      setIsSavingName(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading template editor...</p>
        </div>
      </div>
    );
  }

  if (error || !template || !pdfBlobUrl) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center space-y-4">
          <p className="text-sm text-destructive">
            {error || 'Template not found'}
          </p>
          <Button variant="outline" onClick={() => setLocation('/templates')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b bg-background px-3 py-1.5 sm:px-4 sm:py-2 md:flex-nowrap md:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 sm:h-8 sm:w-8"
            onClick={() => setLocation('/templates')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {isEditingName ? (
            <div className="flex min-w-0 items-center gap-1">
              <Input
                aria-label="Template name"
                autoFocus
                className="h-7 min-w-0 max-w-72 text-xs font-semibold sm:text-sm"
                disabled={isSavingName}
                maxLength={200}
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void saveTemplateName();
                  if (event.key === 'Escape') cancelRenaming();
                }}
              />
              <Button
                aria-label="Save template name"
                className="h-7 w-7 shrink-0"
                disabled={!templateName.trim() || isSavingName}
                onClick={() => void saveTemplateName()}
                size="icon"
                title="Save template name"
              >
                {isSavingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </Button>
              <Button
                aria-label="Cancel renaming template"
                className="h-7 w-7 shrink-0"
                disabled={isSavingName}
                onClick={cancelRenaming}
                size="icon"
                title="Cancel"
                variant="ghost"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-1">
              <h1 className="min-w-0 truncate text-xs font-semibold sm:text-sm">{template.name}</h1>
              <Button
                aria-label="Rename template"
                className="h-7 w-7 shrink-0"
                onClick={startRenaming}
                size="icon"
                title="Rename template"
                variant="ghost"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex w-full flex-wrap items-center justify-end gap-1.5 sm:gap-2 md:w-auto md:flex-nowrap md:shrink-0">
          {template.category && (
            <span className="hidden rounded bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground sm:inline sm:text-xs">
              {template.category}
            </span>
          )}
          {editorToolbar?.hasUnsavedChanges && (
            <>
              <span className="h-2 w-2 rounded-full bg-amber-500 sm:hidden" title="Unsaved changes">
                <span className="sr-only">Unsaved changes</span>
              </span>
              <span className="hidden rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 sm:inline">
                Unsaved changes
              </span>
            </>
          )}
          {editorToolbar && (
            <>
              {!editorToolbar.previewMode && (
                <Button
                  aria-label="Open fields"
                  className="h-8 w-8 px-0 sm:w-auto sm:px-3 md:hidden"
                  onClick={editorToolbar.onOpenPalette}
                  size="sm"
                  title="Fields"
                  variant="outline"
                >
                  <PanelLeft className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Fields</span>
                </Button>
              )}
              {!editorToolbar.previewMode && (
                <Button
                  aria-label="Open properties"
                  className="h-8 w-8 px-0 sm:w-auto sm:px-3 md:hidden"
                  disabled={!editorToolbar.canOpenProperties}
                  onClick={editorToolbar.onOpenProperties}
                  size="sm"
                  title="Properties"
                  variant="outline"
                >
                  <SlidersHorizontal className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Properties</span>
                </Button>
              )}
              <Button
                aria-label="Undo"
                className="h-8 w-8"
                disabled={!editorToolbar.canUndo}
                onClick={editorToolbar.onUndo}
                size="icon"
                title="Undo"
                variant="ghost"
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                aria-label={editorToolbar.previewMode ? 'Edit fields' : 'Preview template'}
                className="h-8 w-8 px-0 sm:w-auto sm:px-3"
                onClick={editorToolbar.onTogglePreview}
                size="sm"
                title={editorToolbar.previewMode ? 'Edit' : 'Preview'}
                variant="outline"
              >
                {editorToolbar.previewMode ? (
                  <>
                    <EyeOff className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Edit</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Preview</span>
                  </>
                )}
              </Button>
              <Button
                aria-label={editorToolbar.isSaving ? 'Saving field placements' : 'Save field placements'}
                className="h-8 w-8 px-0 sm:w-auto sm:px-3"
                disabled={editorToolbar.isSaving || !editorToolbar.hasUnsavedChanges}
                onClick={editorToolbar.onSave}
                size="sm"
                title={editorToolbar.isSaving ? 'Saving...' : 'Save'}
              >
                <Save className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">{editorToolbar.isSaving ? 'Saving...' : 'Save'}</span>
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <TemplateFieldEditor
          pdfUrl={pdfBlobUrl}
          initialPlacements={initialPlacements}
          onSave={handleSave}
          onToolbarStateChange={setEditorToolbar}
        />
      </div>
    </div>
  );
}
